import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, DetailView, getMessageFromError } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { getBeautifiedLocalDate } from '@/core';
import { SqoCertificate } from '../types/sqo-certificate';
import { CertLocation, CertMDRProduct, Certificate } from '@/features/client-certificates/types';
import { getCertificateById } from '../api/getcertificateById';
import { downloadCertificateFromDws } from '@/features/client-certificates/api/downloadFileFromCertificate';
import { printMDRLocations } from '@/features/auditor-visits/api/printMDRLocations';

type CertificateListItemProps = {
  certificate: SqoCertificate;
  visitId: string;
};

type ProductListItem = CertMDRProduct & {
  open?: boolean;
};

type LocationListItem = CertLocation & {
  open?: boolean;
};

type PrintModalProps = {
  certificateId: string;
  visitId: string;
  open: boolean;
  onClose?: () => void;
};

const PrintMDRModal = ({ certificateId, visitId, open, onClose }: PrintModalProps) => {
  const [type, setType] = useState<'DN150' | 'DN150E' | 'Table'>('Table');
  const handleChange = (event: SelectChangeEvent) => {
    setType(event.target.value as 'DN150' | 'DN150E' | 'Table');
  };
  const { t } = useTranslation();
  const printMDRMutation = useMutation({
    mutationFn: printMDRLocations,
    onSuccess: async () => {
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
      onClose?.();
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const handleClick = () => {
    printMDRMutation.mutate({ certificateId, visitId, type });
  };
  return (
    <Modal open={open} onClose={onClose}>
      <Fade in={open}>
        <Card
          sx={{
            width: '600px',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            p: 3,
          }}
        >
          <CardHeader title={'Select report type'} />
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={type}
              label="Type"
              onChange={handleChange}
            >
              <MenuItem value={'DN150'}>DN150</MenuItem>
              <MenuItem value={'DN150E'}>DN150E</MenuItem>
              <MenuItem value={'Table'}>Table</MenuItem>
            </Select>
          </CardContent>
          <CardActions
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              padding: 2,
            }}
          >
            <Button
              onClick={onClose}
              color={'primary'}
              variant={'outlined'}
              // eslint-disable-next-line react/no-children-prop
              children={t('common.Actions.Cancel', {
                defaultValue: 'Cancel',
              })}
            />
            <Button
              data-testid="modal-action-button"
              onClick={handleClick}
              color={'primary'}
              variant={'contained'}
            >
              Print
            </Button>
          </CardActions>
        </Card>
      </Fade>
    </Modal>
  );
};

const CertificateListItem = ({ certificate, visitId }: CertificateListItemProps) => {
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [locationsData, setLocationsData] = useState<LocationListItem[]>([]);
  const [productsData, setProductsData] = useState<ProductListItem[]>([]);
  const { t } = useTranslation();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const downloadFileMutation = useMutation({
    mutationFn: downloadCertificateFromDws,
    onSuccess: async () => {
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const query = useQuery<Certificate, ApiError>(
    ['certificate-id', certificate.id],
    () => getCertificateById(certificate.id),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setLocationsData(data.locations);
        setProductsData(data.mdrProducts);
      },
    }
  );

  const handleLocationClick = (id: string) => {
    const newList = locationsData.map((loc) => {
      if (id === loc.id) {
        const tmp = {
          ...loc,
          open: !loc.open,
        };
        return tmp;
      }
      return loc;
    });

    setLocationsData(newList);
  };

  const renderLocationExpandButton = (loc: LocationListItem) => {
    if (loc.open) {
      return <ExpandLessIcon />;
    }
    if (!loc.open) {
      return <ExpandMoreIcon />;
    }
    return null;
  };
  const handleProdClick = (id: string) => {
    const newList = productsData.map((prod) => {
      if (id === prod.id) {
        const tmp = {
          ...prod,
          open: !prod.open,
        };
        return tmp;
      }
      return prod;
    });

    setProductsData(newList);
  };

  const renderProdExpandButton = (product: ProductListItem) => {
    if (product.open) {
      return <ExpandLessIcon />;
    }
    if (!product.open) {
      return <ExpandMoreIcon />;
    }
    return null;
  };
  return (
    <ListItem
      key={certificate.id}
      sx={{ display: 'flex', width: '100%', flexDirection: 'column', gap: 2 }}
    >
      <ListItemButton onClick={handleExpandClick} sx={{ width: '100%' }}>
        <ListItemText
          primaryTypographyProps={{ fontSize: '1.25rem' }}
          secondaryTypographyProps={{ fontSize: '1rem' }}
          primary={certificate.showAs || 'N/A'}
          secondary={`${t('Status', { defaultValue: 'Status' })}: ${certificate.status || 'N/A'}`}
        />
      </ListItemButton>

      <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
        {query.isError && <p>{query.error.message}</p>}
        {query.isLoading && !query.isError ? (
          <Box>
            <Box
              sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress size={100} />
            </Box>
          </Box>
        ) : (
          query.data && (
            <DetailView
              childrenSx={{ padding: '2rem' }}
              tabs={[
                { id: 'details', title: t('Details', { defaultValue: 'Details' }) },
                { id: 'location', title: t('Locations', { defaultValue: 'Locations' }) },
                { id: 'products', title: t('MDRProducts', { defaultValue: 'MDRProducts' }) },
              ]}
              defaultTab="details"
            >
              {({ TabContent }) => (
                <>
                  <TabContent tab="details">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <List sx={{ display: 'flex', flexDirection: 'column' }}>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('CertificateDocRef', { defaultValue: 'Certificate number' })}
                            secondary={query.data.docReference}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('CertificateOrgTitle', { defaultValue: 'Organization' })}
                            secondary={query.data.title}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('Address', { defaultValue: 'Address' })}
                            secondary={query.data.address}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('Standard', { defaultValue: 'Standart' })}
                            secondary={query.data.standardReference || '/'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('CertificateFirstIssued', {
                              defaultValue: 'CertificateFirstIssued',
                            })}
                            secondary={
                              query.data.firstIssued
                                ? getBeautifiedLocalDate(query.data.firstIssued)
                                : 'N/A'
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('CertificateIssueDate', {
                              defaultValue: 'CertificateIssueDate',
                            })}
                            secondary={
                              query.data.issueDate
                                ? getBeautifiedLocalDate(query.data.issueDate)
                                : 'N/A'
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('CertificateDocVersion', {
                              defaultValue: 'Certificate version',
                            })}
                            secondary={query.data.docVersion}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('CertificateEndDate', {
                              defaultValue: 'CertificateEndDate',
                            })}
                            secondary={
                              query.data.endDate
                                ? getBeautifiedLocalDate(query.data.endDate)
                                : 'N/A'
                            }
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('Scope', { defaultValue: 'Scope' })}
                            secondary={query.data.scope || '/'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('ScopeNative', { defaultValue: 'ScopeNative' })}
                            secondary={query.data.scopeNative || '/'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '1.25rem' }}
                            secondaryTypographyProps={{ fontSize: '1rem' }}
                            primary={t('ScopeEN', { defaultValue: 'ScopeEN' })}
                            secondary={query.data.scopeEn || '/'}
                          />
                        </ListItem>
                      </List>
                      {query.data.dwsDok.filename ? (
                        <Button
                          variant="contained"
                          onClick={() => {
                            downloadFileMutation.mutate({
                              dok: query.data.dwsDok,
                              filename: query.data.dwsDok.originalfilename || query.data.title,
                            });
                          }}
                          fullWidth
                        >
                          {t('Download certificate', { defaultValue: 'Download certificate' })}
                        </Button>
                      ) : (
                        <> </>
                      )}
                    </Box>
                  </TabContent>
                  <TabContent tab="location">
                    <List
                      sx={{ mt: 5, width: '100%', borderRadius: '4px', boxShadow: 1 }}
                      component="div"
                      aria-labelledby="certmdlocation-list"
                    >
                      {locationsData.map((location) => (
                        <Box component="div" key={location.id}>
                          <ListItemButton onClick={() => handleLocationClick(location.id)}>
                            <ListItemText
                              primaryTypographyProps={{ fontSize: '1.25rem' }}
                              secondaryTypographyProps={{ fontSize: '1rem' }}
                              primary={location.name ?? 'N/A'}
                            />
                            {renderLocationExpandButton(location)}
                          </ListItemButton>

                          {locationsData?.length > 0 ? (
                            <Collapse in={location.open} timeout="auto" unmountOnExit>
                              <List component="div" dense disablePadding>
                                <ListItemButton sx={{ pl: 4 }}>
                                  <ListItemText
                                    primaryTypographyProps={{ fontSize: '1.25rem' }}
                                    secondaryTypographyProps={{ fontSize: '1rem' }}
                                    primary={location.address ?? 'N/A'}
                                  />
                                </ListItemButton>
                              </List>
                            </Collapse>
                          ) : null}
                        </Box>
                      ))}
                    </List>
                  </TabContent>
                  <TabContent tab="products">
                    {productsData.length && (
                      <>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setPrintModalOpen(true);
                          }}
                          fullWidth
                        >
                          {t('Print MDR Products', { defaultValue: 'Print MDR Products' })}
                        </Button>
                        {printModalOpen && (
                          <PrintMDRModal
                            onClose={() => setPrintModalOpen(false)}
                            open={printModalOpen}
                            certificateId={certificate.id}
                            visitId={visitId}
                          />
                        )}
                      </>
                    )}
                    <List
                      sx={{ mt: 5, width: '100%', borderRadius: '4px', boxShadow: 1 }}
                      component="div"
                      aria-labelledby="certmdproduct-list"
                    >
                      {productsData.map((product) => (
                        <Box component="div" key={product.id}>
                          <ListItemButton onClick={() => handleProdClick(product.id)}>
                            <ListItemText
                              primaryTypographyProps={{ fontSize: '1.25rem' }}
                              secondaryTypographyProps={{ fontSize: '1rem' }}
                              primary={product.product ?? 'N/A'}
                            />
                            {renderProdExpandButton(product)}
                          </ListItemButton>

                          {productsData?.length > 0 ? (
                            <Collapse in={product.open} timeout="auto" unmountOnExit>
                              <List component="div" dense disablePadding>
                                <ListItemButton sx={{ pl: 4 }}>
                                  <ListItemText
                                    primaryTypographyProps={{ fontSize: '1.25rem' }}
                                    secondaryTypographyProps={{ fontSize: '1rem' }}
                                    primary={product.emdnCode ?? 'N/A'}
                                    secondary={`Model: ${product.model} `}
                                  />
                                </ListItemButton>
                              </List>
                            </Collapse>
                          ) : null}
                        </Box>
                      ))}
                    </List>
                  </TabContent>
                </>
              )}
            </DetailView>
          )
        )}
      </Collapse>
    </ListItem>
  );
};

export default CertificateListItem;
